import React from 'react';

/**
 * The content-box width of an element, as it changes.
 *
 * `Sun.yearChartGeometry` needs a number, and the webapp has no measuring hook —
 * charts here are recharts behind a `ResponsiveContainer`, and the one bespoke
 * SVG (`MoonWithShadow`) takes an explicit `size`. This is the mobile pattern
 * (`onLayout` → state) in the shape the DOM offers.
 *
 * `contentRect` is **already** the content box. The card this page opens from
 * (`StationDashboard/SunInfo.tsx`) used to subtract its own padding from it a
 * second time and drew 16px narrow; the fix was to stop measuring the card —
 * `TwilightBand` observes the band itself. Observe the element the chart fills,
 * never a padded ancestor.
 *
 * Returns 0 until the first observation, and callers must render nothing at 0
 * rather than guessing — a chart drawn at an assumed width is the bug the
 * air-quality chart shipped.
 *
 * Under vitest that is always the case: `vitest.setup.ts` installs a
 * `ResizeObserver` whose `observe` is a `vi.fn()` and never fires a callback,
 * so a component under test measures 0 until the suite stubs one that does.
 */
const useMeasuredWidth = () => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    const node = ref.current;
    // The `undefined` arm is for a non-browser host, not for tests: this repo's
    // `vitest.setup.ts` defines a `ResizeObserver`, so under vitest the guard
    // only ever fires on a null ref.
    if (node === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) {
        setWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width] as const;
};

export default useMeasuredWidth;
